import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { ActivityIngredient } from '@apps/main/modules/recipe/ingredients/activityIngredient';
import type { TokensForReplacement } from '@apps/main/new-desktop/pages/tripOverview/models';

import { trackErrorAddingBasket } from '@libs/google-analytics-events';
import {
  isAvailableActivityIngredientResponse,
  isAvailableHotelIngredientResponse,
  isAvailableTransportIngredientResponse,
  isHotelIngredientResponse,
} from '@libs/search-service-smart-search';
import { SemboDate, DateFormat } from '@libs/sembo-dates';
import type {
  ActivityBookableOptionTicket,
  ActivityIngredientResponse,
  ActivityToBook,
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  HotelIngredientResponse,
  ItineraryIngredientResponse,
  ItineraryMainIngredientResponse,
  OwnStayIngredientResponse,
  SelectedTicketInfo,
} from '@libs/trip-planner-interfaces';

const { t } = i18n.global;

export const enum BookingErrorType {
  CommunicationError = 'CommunicationError',
  WrongNumberOfTokens = 'WrongNumberOfTokens',
}
export interface BookingError {
  errorType: BookingErrorType;
  header: string;
  descrtiption: string;
}

export type BookOutcome =
  | { outcome: 'success' }
  | {
      outcome: 'token_error';
      errorType: BookingErrorType.WrongNumberOfTokens;
      errorMessage: string;
    }
  | {
      outcome: 'basket_error';
      errorType: BookingErrorType.CommunicationError;
      errorMessage: string;
    };

export function handleBookingError(
  error: BookingErrorType
): BookingError | null {
  trackErrorAddingBasket();
  if (error === BookingErrorType.CommunicationError) {
    return {
      errorType: BookingErrorType.CommunicationError,
      header: t(`tips.CommunicationError.title`),
      descrtiption: t(`tips.CommunicationError.text`),
    };
  }

  if (error === BookingErrorType.WrongNumberOfTokens) {
    return {
      errorType: BookingErrorType.WrongNumberOfTokens,
      header: t(`tips.WrongNumberOfTokens.title`),
      descrtiption: t(`tips.WrongNumberOfTokens.text`),
    };
  }

  return null;
}

export function replaceTokensBeforeGoingToCheckout(
  currentTokens: [string][],
  tokenForReplacements: TokensForReplacement[]
): [string][] {
  return currentTokens.map((token) => {
    const tokenForReplacement = tokenForReplacements.find(
      (tokenForReplacement) => tokenForReplacement.oldToken === token[0]
    );

    if (tokenForReplacement) {
      return [tokenForReplacement.newToken];
    }

    return token;
  });
}

export function findActivityInRecipe(
  subIngredients: ActivityIngredient[],
  code: string,
  activityDate: string,
  dateOffset: number
): ActivityIngredient | undefined {
  return subIngredients.find(
    (activityIngredient) =>
      activityIngredient.code === code &&
      (activityIngredient.selectedTicketDate
        ? activityIngredient.selectedTicketDate === activityDate
        : activityIngredient.offset === dateOffset)
  );
}

export function mapTicketToSelectedTicketInfo(
  ticket: ActivityBookableOptionTicket
): SelectedTicketInfo {
  return {
    timeSlotId: ticket.timeSlotId,
    rateId: ticket.rateId,
    quantity: ticket.quantity,
    ageRangeId: ticket.ageRangeId,
    supplierToken: ticket.supplierToken,
  };
}

export function buildActivitiesToBook(
  stayResponses: (HotelIngredientResponse | OwnStayIngredientResponse)[],
  recipeSubIngredients: ActivityIngredient[]
): ActivityToBook[] {
  return stayResponses.flatMap((hotelResponse) =>
    hotelResponse.subIngredientResponse.reduce(
      (
        availableActivitiesToBook: ActivityToBook[],
        activitySubIngredient: ActivityIngredientResponse
      ) => {
        if (isAvailableActivityIngredientResponse(activitySubIngredient)) {
          const code = activitySubIngredient.suggested.code;

          const dateOffset = new SemboDate(activitySubIngredient.date).diff(
            hotelResponse.date,
            'day'
          );

          const activityDate = new SemboDate(activitySubIngredient.date).format(
            DateFormat.DateReverse
          );

          const activity = findActivityInRecipe(
            recipeSubIngredients,
            code,
            activityDate,
            dateOffset
          );

          if (!activity) {
            throw new Error('Activity not found in recipe');
          }

          availableActivitiesToBook.push({
            code,
            date: activitySubIngredient.date,
            tickets: activitySubIngredient.suggested.tickets.map(
              mapTicketToSelectedTicketInfo
            ),
          });
        }

        return availableActivitiesToBook;
      },
      []
    )
  );
}

export function isBookableTransportIngredientResponse(
  ingredientResponse: ItineraryIngredientResponse
): ingredientResponse is
  | AvailableFlightIngredientResponse
  | AvailableFerryIngredientResponse {
  return (
    isAvailableTransportIngredientResponse(ingredientResponse) &&
    ingredientResponse.totalPrice.amount > 0 &&
    ingredientResponse.legIndex === 0
  );
}

export function getHotelAndFlightBookingTokens(
  itinerary: ItineraryMainIngredientResponse[]
): string[] {
  return itinerary.reduce((tokens: string[], ingredientResponse) => {
    if (isAvailableHotelIngredientResponse(ingredientResponse)) {
      tokens.push(ingredientResponse.suggested.addToBasketToken);
    }
    if (isBookableTransportIngredientResponse(ingredientResponse)) {
      tokens.push(
        `Type=flight&Identifier=${ingredientResponse.addToBasketToken}`
      );
    }

    return tokens;
  }, []);
}

export function validateTokenCounts(
  activityTokens: string[],
  activitiesToBook: ActivityToBook[],
  hotelAndFlightTokens: string[],
  itinerary: ItineraryMainIngredientResponse[]
): void {
  if (activityTokens.length !== activitiesToBook.length) {
    throw new Error(
      'Activity booking tokens do not match number of activities'
    );
  }

  const bookableIngredients = itinerary.filter(
    (ingredient) =>
      isHotelIngredientResponse(ingredient) ||
      isBookableTransportIngredientResponse(ingredient)
  );

  if (hotelAndFlightTokens.length !== bookableIngredients.length) {
    throw new Error(
      'Hotel and flight booking tokens do not match number of ingredients'
    );
  }
}
